import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "antd";
import RevstackFiltersLayout from "@/pages/revstack-dashboards/RevstackFiltersLayout";
import RevstackTabsSection from "@/pages/revstack-dashboards/RevstackTabsSection";
import { updateTitle } from "@features/titleSlice";
import { setIsLoader } from "@features/loaderSlice";
import {
    updateRevstackActiveTab,
    resetRevstackActiveTab,
    resetRevstackDashboardWidgetData,
    resetRelativeFilters,
    resetMainFilters,
    appendRelativeFilters,
} from "@features/revstackWidgetsSlice";
import RevstackAIInsights from "./RevstackAIInsights";
import {
    DASHBOARD_TYPES,
    IRevstackActiveTab,
    IRevstackChannelState,
    IRevstackDashboardTab,
    IRevstackRelativeFilters,
    TActiveClientId,
    RevstackCASectionIds,
    RevstackDashboardModules,
    IRevstackWidgetData,
    RevstackDashboardSubModules,
    CA_DD_MODULES,
} from "@/ts/common-types";
import { useGetDashboardConfig } from "@hooks/AllCustomHooks";
import { titleCase } from "@ts/utils";
import RevstackDashboardGridLayout from "./RevstackDashboardGridLayout";
import "@/scss/revstack-dashboards.scss";
import { keys } from "underscore";
import RevstackRelativeFiltersLayout from "./RevstackRelativeFiltersLayout";
import { REVSTACK_RELATIVE_FILTERS } from "@/ts/constants";

const RevstackDashboards: React.FC<{
    module: RevstackDashboardModules;
    showCategoryFilters?: boolean;
}> = ({ module, showCategoryFilters }) => {
    const dispatch = useDispatch();
    const revstackDashboardWidgetData = useSelector(
        (state: { revstackWidgetData: IRevstackWidgetData }) => state.revstackWidgetData.dashboard
    );
    const revstackActiveTab = useSelector(
        (state: { revstackActiveTab: IRevstackActiveTab }) => state.revstackActiveTab.value
    );
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const relativeFilters = useSelector((state: { relativeFilters: { value: IRevstackRelativeFilters } }) => state.relativeFilters.value);

    const revstackChannel = useSelector(
        (state: IRevstackChannelState) => state.revstackChannel.value
    );
    const revstackDashboardTabs = useSelector(
        (state: { revstackDashboardTabs: Record<string, IRevstackDashboardTab[]> }) => state.revstackDashboardTabs
    );

    const setActiveTab = useCallback(
        (tab: RevstackDashboardSubModules | CA_DD_MODULES) => {
            dispatch(updateRevstackActiveTab(tab));
        },
        [dispatch]
    );

    const { widgetConfig: config } = useGetDashboardConfig(
        module,
        DASHBOARD_TYPES.BASE,
        revstackActiveTab as RevstackDashboardSubModules
    );

    useEffect(() => {
        dispatch(setIsLoader(false));
        dispatch(updateTitle(titleCase(module)));
        dispatch(resetRevstackActiveTab());
        return () => {
            dispatch(resetRevstackDashboardWidgetData());
        };
    }, [activeClientId, revstackChannel, module, dispatch]);

    const { bannerConfig, nonBannerConfig } = useMemo(() => {
        return config.reduce(
            (acc: any, widgetConfig: any) => {
                if (widgetConfig.sectionId === RevstackCASectionIds.BANNER) {
                    acc.bannerConfig.push(widgetConfig);
                } else {
                    acc.nonBannerConfig.push(widgetConfig);
                }
                return acc;
            },
            { bannerConfig: [], nonBannerConfig: [] }
        );
    }, [config]);

    const isBannerSticky = useMemo(() => {
        return bannerConfig.some((widgetConfig: any) => widgetConfig?.config?.layout?.sticky);
    }, [bannerConfig]);


    const renderActiveTabContent = useMemo(() => {
        if (revstackActiveTab === "ai-insights") {
            return <RevstackAIInsights layoutType="main" />;
        }

        return (
            <RevstackDashboardGridLayout
                moduleData={revstackDashboardWidgetData}
                config={nonBannerConfig}
            />
        );
    }, [
        revstackDashboardWidgetData,
        revstackActiveTab,
        nonBannerConfig,
        module,
    ]);

    const items = useMemo(
        () =>
            revstackDashboardTabs?.[module]?.map((tab: IRevstackDashboardTab) => ({
                key: tab.key,
                label: tab.label,
                children:
                    tab.key === revstackActiveTab
                        ? renderActiveTabContent
                        : null,
            })),
        [
            revstackDashboardTabs,
            module,
            revstackActiveTab,
            renderActiveTabContent,
        ]
    );

    /*
     ! Note[DND]:
     * If a user directly visits redirectioned modules such then we have to show the filters UI
     * As soon as the user selects the filters the config UI will be rendered and 'showFiltersLayout' will be false
     * In future once there are more modules like product analysis we can then create a module and filter mapping for the same
     */

    const isProductAnalysisWithEmptySkus = useMemo(() => {
        if (module !== RevstackDashboardModules.PRODUCT_ANALYSIS) {
            return false;
        }
        const filterKeys = keys(relativeFilters);
        return filterKeys?.includes(REVSTACK_RELATIVE_FILTERS.SKUS) && relativeFilters?.skus?.length === 0;
    }, [module, relativeFilters]);

    const showRelativeFilters = isProductAnalysisWithEmptySkus;

    const shouldShowCategoryFilters = useMemo(() => {
        return module === RevstackDashboardModules.PRODUCT_ANALYSIS
            ? isProductAnalysisWithEmptySkus
            : showCategoryFilters;
    }, [module, isProductAnalysisWithEmptySkus, showCategoryFilters]);

    useEffect(() => {
        return () => {
            dispatch(resetMainFilters());
            dispatch(resetRelativeFilters());
        }
    }, [dispatch]);

        return (
            <RevstackFiltersLayout showCategoryFilters={shouldShowCategoryFilters}>
                <Row>
                    <Col span={24}>
                        {showRelativeFilters ? (
                            <Row className="w-100 p-4 py-3">
                                <RevstackRelativeFiltersLayout />
                            </Row>
                        ) : (
                            <>
                                <Row className={`w-100 ${isBannerSticky ? 'revstack-banner-sticky' : ''}`}>
                                    {bannerConfig.map(
                                        (widgetConfig: any, idx: number) => {
                                            const WidgetComponent =
                                                widgetConfig.widget;
                                            return (
                                                <div
                                                    key={`${widgetConfig.sectionId}-${idx}`}
                                                    className="w-100"
                                                >
                                                    <WidgetComponent
                                                        widgetConfig={widgetConfig}
                                                        widgetData={
                                                            revstackDashboardWidgetData
                                                        }
                                                        title={widgetConfig.title}
                                                    />
                                                </div>
                                            );
                                        }
                                    )}
                                </Row>
                                <Row
                                    className="w-100 revstack-layout-whitespace pt-1"
                                    justify="space-between"
                                    align="middle"
                                >
                                    <div className="w-100 px-4">
                                        <RevstackTabsSection
                                            {...{
                                                items,
                                                setActiveTab,
                                                activeTab: revstackActiveTab,
                                            }}
                                            type="default"
                                        />
                                    </div>
                                </Row>
                            </>
                        )}
                    </Col>
                </Row>
            </RevstackFiltersLayout>
        );
    };

export default RevstackDashboards;